import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { NavLink, useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import { userSignUp } from 'redux-thunk/thunk/auth.thunk'
import { openErrorToaster } from 'helpers/toaster.helpers'
import ErrorMessage from 'components/layout/ErrorMessage/index'
import Loader from 'components/ui-kit/Loader/index'
import { LOADER_HANDLER_TYPES, ROUTE_PATHS } from 'constants/index'
import SocialLinks from '../SocialLinks/index'
import { useQuery } from 'hooks/useQuery'
import { EyeCloseIcon, EyeIcon } from 'components/ui-kit/Icons/svg/index'

const Signup = ({ setShowVerifyEmailPopup }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const query = useQuery()
  const [showPassword, setShowPassword] = useState({
    showPass: false,
    showConfirmPass: false
  })
  const { [LOADER_HANDLER_TYPES.submit]: loading } = useSelector(state => state.loader)

  const schema = yup.object().shape({
    userName: yup
      .string()
      .required(`${t('signupErrorsUserNameRequired')}`)
      .min(5, `${t('signupErrorsUserNameMinLength')}`)
      .max(20, `${t('signupErrorsUserNameMaxLength')}`)
      .matches(/^\w+$/, t('signupErrorsUserNamePattern')),
    email: yup
      .string()
      .required(t('signupErrorsEmailRequired'))
      .matches(
        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
        t('signupErrorsEmailPattern')
      ),
    password: yup
      .string()
      .required(`${t('signupErrorsPasswordRequired')}`)
      .min(8, `${t('signupErrorsPasswordPattern')}`)
      .max(16, `${t('signupErrorsPasswordPattern')}`)
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        t('signupErrorsPasswordPattern')
      )
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit'
  })

  const handleRegisterSubmit = async (userDetails) => {
    const affiliateCode = query.get('affiliateCode')
    const referralCode = query.get('referralCode')
    if (affiliateCode) {
      userDetails.affiliatedBy = affiliateCode
    }
    if (referralCode) {
      userDetails.referrerCode = referralCode
      userDetails.referralLink = window?.location?.href
    }
    try {
      dispatch(userSignUp({ userDetails, setShowVerifyEmailPopup }))
      reset()
    } catch (e) {
      openErrorToaster({ message: e.message })
    }
  }

  useEffect(() => {
    return () => {
      if (location.pathname === ROUTE_PATHS.SIGNUP) {
        history.push(ROUTE_PATHS.HOME)
      }
    }
  }, [])

  return (
    <div role='tabpanel'>
      <div className='pb-5 pb-sm-0'>
        <form onSubmit={handleSubmit(handleRegisterSubmit)}>
          <div className='mb-3 form-group'>
            <label htmlFor='user_name' className='form-label text-uppercase'>
              {t('signupUsername')}
            </label> <span className='color-red'>*</span>
            <input
              type='text'
              placeholder={t('signupUsername')}
              className={`box-border w-full h-10 px-3 py-1 text-base text-[var(--white)] bg-[var(--darkGray)] border-[1px] border-solid border-transparent rounded-lg shadow-[inset_0_1px_0_rgb(var(--pm-gold)/0.06)] outline-none placeholder:!text-[var(--gray-20)] animate-pm-fade-in-up motion-reduce:animate-none [transition:box-shadow_var(--pm-duration-fast,0.16s)_var(--pm-ease,ease),transform_var(--pm-duration,0.24s)_var(--pm-ease-spring,ease)] [background:linear-gradient(145deg,#472e00,#090b0b_25%)_padding-box,linear-gradient(135deg,#8a6500,#ffd700,#b8860b,#b8860b,#8a6500)_border-box] hover:-translate-y-0.5 hover:[background:linear-gradient(145deg,#472e00,#090b0b_35%)_padding-box,linear-gradient(135deg,#8a6500,#ffd700,#b8860b,#b8860b,#8a6500)_border-box] hover:shadow-[inset_0_1px_0_rgb(var(--pm-gold)/0.1),0_6px_18px_rgb(var(--color-shadow)/0.18)] focus:text-[var(--white)] focus:outline-none focus:shadow-none focus-within:shadow-none disabled:bg-[var(--gray-90)] disabled:text-[var(--white)] disabled:cursor-not-allowed read-only:bg-[var(--gray-90)] read-only:text-[var(--white)] ${errors?.userName ? 'error-input' : ''}`}
              id='user_name'
              name='userName'
              {...register('userName')}
              aria-describedby=''
              maxLength={50}
            />
            {errors && errors.userName && (
              <div className='error-container'>
                <ErrorMessage
                  className='color-red text-danger error-msg login-input ms-2 d-flex'
                  message={errors.userName.message}
                />
              </div>
            )}
          </div>
          <div className='mb-3 form-group'>
            <label htmlFor='email' className='form-label text-uppercase'>
              {t('email')}
            </label> <span className='color-red'>*</span>
            <input
              type='email'
              placeholder={t('emailPlaceholder')}
             className={`box-border w-full h-10 px-3 py-1 text-base text-[var(--white)] bg-[var(--darkGray)] border-[1px] border-solid border-transparent rounded-lg shadow-[inset_0_1px_0_rgb(var(--pm-gold)/0.06)] outline-none placeholder:!text-[var(--gray-20)] animate-pm-fade-in-up motion-reduce:animate-none [transition:box-shadow_var(--pm-duration-fast,0.16s)_var(--pm-ease,ease),transform_var(--pm-duration,0.24s)_var(--pm-ease-spring,ease)] [background:linear-gradient(145deg,#472e00,#090b0b_25%)_padding-box,linear-gradient(135deg,#8a6500,#ffd700,#b8860b,#b8860b,#8a6500)_border-box] hover:-translate-y-0.5 hover:[background:linear-gradient(145deg,#472e00,#090b0b_35%)_padding-box,linear-gradient(135deg,#8a6500,#ffd700,#b8860b,#b8860b,#8a6500)_border-box] hover:shadow-[inset_0_1px_0_rgb(var(--pm-gold)/0.1),0_6px_18px_rgb(var(--color-shadow)/0.18)] focus:text-[var(--white)] focus:outline-none focus:shadow-none focus-within:shadow-none disabled:bg-[var(--gray-90)] disabled:text-[var(--white)] disabled:cursor-not-allowed read-only:bg-[var(--gray-90)] read-only:text-[var(--white)] ${errors?.email ? 'error-input' : ''}`}
              id='email'
              aria-describedby=''
              name='email'
              maxLength={50}
              {...register('email')}
            />
            {errors && errors.email && (
              <div className='error-container'>
                <ErrorMessage
                  className='color-red text-danger error-msg login-input ms-2 d-flex'
                  message={errors.email.message}
                />
              </div>
            )}
          </div>
          <div className='mb-4 form-group'>
            <label
              htmlFor='user_password'
              className='form-label text-uppercase'
            >
              {t('signupPassword')}
            </label> <span className='color-red'>*</span>
            <input
              type={showPassword.showPass ? 'text' : 'password'}
              placeholder={t('********')}
              className={`box-border w-full h-10 px-3 py-1 text-base text-[var(--white)] bg-[var(--darkGray)] border-[1px] border-solid border-transparent rounded-lg shadow-[inset_0_1px_0_rgb(var(--pm-gold)/0.06)] outline-none placeholder:!text-[var(--gray-20)] animate-pm-fade-in-up motion-reduce:animate-none [transition:box-shadow_var(--pm-duration-fast,0.16s)_var(--pm-ease,ease),transform_var(--pm-duration,0.24s)_var(--pm-ease-spring,ease)] [background:linear-gradient(145deg,#472e00,#090b0b_25%)_padding-box,linear-gradient(135deg,#8a6500,#ffd700,#b8860b,#b8860b,#8a6500)_border-box] hover:-translate-y-0.5 hover:[background:linear-gradient(145deg,#472e00,#090b0b_35%)_padding-box,linear-gradient(135deg,#8a6500,#ffd700,#b8860b,#b8860b,#8a6500)_border-box] hover:shadow-[inset_0_1px_0_rgb(var(--pm-gold)/0.1),0_6px_18px_rgb(var(--color-shadow)/0.18)] focus:text-[var(--white)] focus:outline-none focus:shadow-none focus-within:shadow-none disabled:bg-[var(--gray-90)] disabled:text-[var(--white)] disabled:cursor-not-allowed read-only:bg-[var(--gray-90)] read-only:text-[var(--white)] ${errors?.password ? 'error-input' : ''}`}
              id='user_password'
              name='password'
              maxLength={50}
              {...register('password')}
            />
            <a
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  showPass: !prevState.showPass
                }))}
              className='show-password'
            >
              {!showPassword.showPass
                ? (
                  <EyeIcon />
                  )
                : (
                  <EyeCloseIcon />
                  )}
            </a>
            {errors && errors.password && (
              <div className='error-container'>
                <ErrorMessage
                  className='color-red text-danger error-msg login-input ms-2 d-flex'
                  message={errors.password.message}
                />
              </div>
            )}
          </div>
          <div className='auth-btn-wrap'>
            <button
              type='submit'
              className='btn btn-secondary w-full'
              disabled={loading}
            >
              {loading
                ? (
                  <Loader variant={LOADER_HANDLER_TYPES.submit} />
                  )
                : (
                  <span>{t('startplaying')}</span>
                  )}
            </button>
          </div>
          <div className='auth-socail-wrap'>
            <div className='auth-social-heading'>
              <h6>
                <span>{t('orContinueWith')}</span>
              </h6>
            </div>
          </div>
          <SocialLinks isLoginSignup />
          <div className='auth-mob-instructions d-block d-lg-none accordion pb-5 pb-md-0'>
            <p>
              {t('usersConsent')}
              <NavLink to='#'>{t('termsOfService')}</NavLink>
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Signup
